import type { OperationTypeDefinition } from '@/graph/domain/types';

const now = new Date().toISOString();
const stub = { id: '', email: '', name: 'System' };

/**
 * Fixture catalog entries for development and fallback.
 * Based on the Angular client's STEP_PARAMETERS + a few custom ops.
 */
export const catalogFixture: OperationTypeDefinition[] = [
  // ── TRANSFORM ───────────────────────────────────────────────────────────────
  {
    id: 'centrifugation',
    uid: 'DE-0000-OP-0001',
    operationName: 'Centrifugation',
    operationType: 'TRANSFORM',
    subType: 'transformation',
    display: true,
    isArchived: false,
    isSoftDeleted: false,
    isLocked: false,
    versionNumber: 1,
    isCurrentVersion: true,
    versions: [],
    durationRequired: false,
    durationAllow: true,
    canEditCheckpoints: false,
    translationTemplate: 'Centrifuge {materialName} at {parameters} for {duration}',
    parameters: [
      { id: 'Acceleration', parameterName: 'Acceleration', fieldType: 'units', order: 0, required: false, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['rpm', 'g', 'rcf'] },
      { id: 'Temperature', parameterName: 'Temperature', fieldType: 'units', order: 1, required: false, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['°C', '°F', 'K'] },
    ],
    checkpoints: [],
    operationTypeConfig: { allowYield: true },
    workspaceId: '', addedBy: stub, createdOn: now, updatedOn: now,
  },
  {
    id: 'incubation',
    uid: 'DE-0000-OP-0002',
    operationName: 'Incubation',
    operationType: 'TRANSFORM',
    subType: 'transformation',
    display: true,
    isArchived: false,
    isSoftDeleted: false,
    isLocked: false,
    versionNumber: 1,
    isCurrentVersion: true,
    versions: [],
    durationRequired: true,
    durationAllow: true,
    canEditCheckpoints: false,
    translationTemplate: 'Incubate {materialName} at {parameters} for {duration}',
    parameters: [
      { id: 'ShakingSpeed', parameterName: 'Shaking Speed', fieldType: 'units', order: 0, required: false, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['rpm'] },
      { id: 'CO2percent', parameterName: 'CO2 percent', fieldType: 'number', order: 1, required: false, uniqueAcrossWorkspace: false, allowSpecialCharacters: true },
      { id: 'Temperature', parameterName: 'Temperature', fieldType: 'units', order: 2, required: false, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['°C', '°F', 'K'] },
    ],
    checkpoints: [],
    operationTypeConfig: { allowYield: false },
    workspaceId: '', addedBy: stub, createdOn: now, updatedOn: now,
  },
  {
    id: 'mix',
    uid: 'DE-0000-OP-0003',
    operationName: 'Mix',
    operationType: 'TRANSFORM',
    subType: 'transformation',
    display: true,
    isArchived: false,
    isSoftDeleted: false,
    isLocked: false,
    versionNumber: 1,
    isCurrentVersion: true,
    versions: [],
    durationRequired: false,
    durationAllow: true,
    canEditCheckpoints: false,
    translationTemplate: 'Mix {materialName} at {parameters}',
    parameters: [
      { id: 'Parameter', parameterName: 'Speed', fieldType: 'units', order: 0, required: false, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['rpm'] },
    ],
    checkpoints: [],
    operationTypeConfig: { allowYield: false },
    workspaceId: '', addedBy: stub, createdOn: now, updatedOn: now,
  },
  {
    id: 'thermocycling',
    uid: 'DE-0000-OP-0004',
    operationName: 'Thermocycling',
    operationType: 'TRANSFORM',
    subType: 'transformation',
    display: true,
    isArchived: false,
    isSoftDeleted: false,
    isLocked: false,
    versionNumber: 1,
    isCurrentVersion: true,
    versions: [],
    durationRequired: true,
    durationAllow: true,
    canEditCheckpoints: false,
    translationTemplate: 'Thermocycle {materialName} for {duration}',
    parameters: [],
    checkpoints: [],
    operationTypeConfig: { allowYield: false },
    workspaceId: '', addedBy: stub, createdOn: now, updatedOn: now,
  },

  // ── MEASURE ─────────────────────────────────────────────────────────────────
  {
    id: 'absorbance',
    uid: 'DE-0000-OP-0010',
    operationName: 'Absorbance',
    operationType: 'MEASURE',
    subType: 'measurement',
    display: true,
    isArchived: false,
    isSoftDeleted: false,
    isLocked: false,
    versionNumber: 1,
    isCurrentVersion: true,
    versions: [],
    durationRequired: false,
    durationAllow: false,
    canEditCheckpoints: false,
    translationTemplate: 'Measure absorbance of {materialName} at {parameters}',
    parameters: [
      { id: 'Wavelength', parameterName: 'Wavelength', fieldType: 'units', order: 0, required: true, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['nm'] },
      { id: 'Frequency', parameterName: 'Frequency of measurement', fieldType: 'units', order: 1, required: false, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['Hz'] },
    ],
    checkpoints: [
      { id: 'cp-abs-1', checkpointName: 'Actual Wavelength', fieldType: 'number', required: true, allowMultiple: false, allowSpecialCharacters: true, uniqueAcrossWorkspace: false },
      { id: 'cp-abs-2', checkpointName: 'Operator', fieldType: 'user', required: false, allowMultiple: false, allowSpecialCharacters: true, uniqueAcrossWorkspace: false },
    ],
    operationTypeConfig: { allowYield: false },
    workspaceId: '', addedBy: stub, createdOn: now, updatedOn: now,
  },
  {
    id: 'sequencing',
    uid: 'DE-0000-OP-0011',
    operationName: 'Sequencing',
    operationType: 'MEASURE',
    subType: 'measurement',
    display: true,
    isArchived: false,
    isSoftDeleted: false,
    isLocked: false,
    versionNumber: 1,
    isCurrentVersion: true,
    versions: [],
    durationRequired: false,
    durationAllow: false,
    canEditCheckpoints: false,
    translationTemplate: 'Sequence {materialName}',
    parameters: [],
    checkpoints: [],
    operationTypeConfig: { allowYield: false },
    workspaceId: '', addedBy: stub, createdOn: now, updatedOn: now,
  },
  {
    id: 'weigh',
    uid: 'DE-0000-OP-0012',
    operationName: 'Weigh',
    operationType: 'MEASURE',
    subType: 'measurement',
    display: true,
    isArchived: false,
    isSoftDeleted: false,
    isLocked: false,
    versionNumber: 1,
    isCurrentVersion: true,
    versions: [],
    durationRequired: false,
    durationAllow: false,
    canEditCheckpoints: false,
    translationTemplate: 'Weigh {materialName}',
    parameters: [
      { id: 'Weight', parameterName: 'Weight', fieldType: 'units', order: 0, required: true, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['g', 'kg', 'mg', 'µg'] },
    ],
    checkpoints: [],
    operationTypeConfig: { allowYield: false },
    workspaceId: '', addedBy: stub, createdOn: now, updatedOn: now,
  },
  {
    id: 'fluorescence',
    uid: 'DE-0000-OP-0013',
    operationName: 'Fluorescence',
    operationType: 'MEASURE',
    subType: 'measurement',
    display: true,
    isArchived: false,
    isSoftDeleted: false,
    isLocked: false,
    versionNumber: 1,
    isCurrentVersion: true,
    versions: [],
    durationRequired: false,
    durationAllow: false,
    canEditCheckpoints: false,
    translationTemplate: 'Measure fluorescence of {materialName} at {parameters}',
    parameters: [
      { id: 'ExcitationWavelength', parameterName: 'Excitation wavelength', fieldType: 'units', order: 0, required: true, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['nm'] },
      { id: 'EmissionWavelength', parameterName: 'Emission wavelength', fieldType: 'units', order: 1, required: true, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['nm'] },
      { id: 'Frequency', parameterName: 'Frequency of measurement', fieldType: 'units', order: 2, required: false, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['Hz'] },
    ],
    checkpoints: [],
    operationTypeConfig: { allowYield: false },
    workspaceId: '', addedBy: stub, createdOn: now, updatedOn: now,
  },
  {
    id: 'gel-electrophoresis',
    uid: 'DE-0000-OP-0014',
    operationName: 'Gel Electrophoresis',
    operationType: 'MEASURE',
    subType: 'measurement',
    display: true,
    isArchived: false,
    isSoftDeleted: false,
    isLocked: false,
    versionNumber: 1,
    isCurrentVersion: true,
    versions: [],
    durationRequired: false,
    durationAllow: true,
    canEditCheckpoints: false,
    translationTemplate: 'Run gel electrophoresis on {materialName} at {parameters}',
    parameters: [
      { id: 'Volume', parameterName: 'Volume', fieldType: 'units', order: 0, required: false, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['mL', 'µL'] },
      { id: 'Voltage', parameterName: 'Voltage', fieldType: 'units', order: 1, required: false, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['V'] },
    ],
    checkpoints: [],
    operationTypeConfig: { allowYield: false },
    workspaceId: '', addedBy: stub, createdOn: now, updatedOn: now,
  },

  // ── COMPLEX ─────────────────────────────────────────────────────────────────
  {
    id: 'hplc-standard',
    uid: 'DE-0000-OP-HPLC',
    operationName: 'HPLC',
    operationType: 'COMPLEX',
    display: true,
    isArchived: false,
    isSoftDeleted: false,
    isLocked: false,
    versionNumber: 1,
    isCurrentVersion: true,
    versions: [],
    durationRequired: true,
    durationAllow: true,
    canEditCheckpoints: false,
    translationTemplate: 'Run HPLC on {materialName}',
    parameters: [
      { id: 'ColumnType', parameterName: 'Column Type', fieldType: 'dropdown', order: 0, required: false, uniqueAcrossWorkspace: false, allowSpecialCharacters: false, dropDownList: [{ id: 'c18', label: 'C18' }, { id: 'c8', label: 'C8' }] },
      { id: 'FlowRate', parameterName: 'Flow Rate', fieldType: 'units', order: 1, required: false, uniqueAcrossWorkspace: false, allowSpecialCharacters: true, parameterTags: ['mL/min'] },
    ],
    checkpoints: [],
    operationTypeConfig: { allowYield: true, allowMultipleInputs: true, allowMultipleOutputs: true, allowIOMapping: true },
    workspaceId: '', addedBy: stub, createdOn: now, updatedOn: now,
  },
  {
    id: 'cell-lysis',
    uid: 'DE-0000-OP-LYSIS',
    operationName: 'Cell Lysis',
    operationType: 'COMPLEX',
    display: true,
    isArchived: false,
    isSoftDeleted: false,
    isLocked: false,
    versionNumber: 1,
    isCurrentVersion: true,
    versions: [],
    durationRequired: false,
    durationAllow: true,
    canEditCheckpoints: false,
    translationTemplate: 'Lyse {materialName}',
    parameters: [],
    checkpoints: [],
    operationTypeConfig: { allowYield: false, allowMultipleInputs: true, allowMultipleOutputs: true, allowIOMapping: true },
    workspaceId: '', addedBy: stub, createdOn: now, updatedOn: now,
  },
];
